import React, {ReactNode, useEffect, useRef} from 'react'
import {Controller, Control, FieldErrors, FieldError} from 'react-hook-form'
import {useIntl} from 'react-intl'
import {Input, Textarea, Select, Radio, Checkbox} from '_uiComponents'

type FormValues = {
    name: string
    surname: string
    email: string
    'email-phone': string
    phone: string
    'select-test-1': string
    'select-test-2': string
    password: string
    message: string
    radio: string
    checkbox: string
    men: string
    women: string
    kids: string
    eyewear: string
    fragrances: string
    lifestyle: string
    preferenceNewsletterConsent: string
}

interface FormFactoryProps {
    control: Control<FormValues>
    type: string
    name: keyof FormValues
    label: string | ReactNode
    text?: string | ReactNode
    errors: FieldErrors<FormValues>
    placeholder?: string
    options?: {label: string; value: string}[]
    defaultValue?: string | undefined
    isRequired?: boolean
    isDisabled?: boolean
    variant?: string
    size?: string
    id?: string
    onChange?: () => void
}

const FormFactory = ({
    control,
    type,
    name,
    label = '',
    errors,
    placeholder,
    options,
    defaultValue,
    isRequired = true,
    isDisabled = false,
    variant,
    size,
    id,
    onChange,
}: FormFactoryProps) => {
    const {formatMessage} = useIntl()
    const emptyFieldErrorLabel = formatMessage(
        {
            id: 'error.field.required',
            defaultMessage: '*Error: field {0} is mandatory',
        },
        {
            0: label,
        },
    )

    const ref = useRef<HTMLInputElement>(null)

    useEffect(() => {
        if (errors[name] && ref?.current) {
            ref?.current?.scrollIntoView({behavior: 'smooth', block: 'center'})
        }
    }, [errors, name])

    const renderInput = (additionalRules?: object) => {
        return (
            <Controller
                control={control}
                name={name}
                defaultValue={defaultValue}
                rules={
                    isRequired
                        ? {
                              required: emptyFieldErrorLabel,
                              ...additionalRules,
                          }
                        : additionalRules
                }
                render={({field}) => {
                    switch (type) {
                        case 'textarea':
                            return (
                                <Textarea
                                    {...field}
                                    label={label}
                                    placeholder={placeholder}
                                    ref={ref}
                                    isDisabled={isDisabled}
                                    isRequired={isRequired}
                                    errorMessage={(errors[name] as FieldError)?.message}
                                    onChange={(value) => {
                                        field.onChange(value)
                                        if (onChange) {
                                            onChange()
                                        }
                                    }}
                                    data-intent-redact
                                />
                            )

                        case 'select':
                            return (
                                <Select
                                    {...field}
                                    label={label}
                                    placeholder={placeholder}
                                    ref={ref}
                                    options={options || []}
                                    isDisabled={isDisabled}
                                    isRequired={isRequired}
                                    errorMessage={(errors[name] as FieldError)?.message}
                                    onChange={(value) => {
                                        field.onChange(value)
                                        if (onChange) {
                                            onChange()
                                        }
                                    }}
                                    data-intent-redact
                                />
                            )

                        case 'radio':
                            return (
                                <Radio
                                    {...field}
                                    size={size}
                                    ref={ref}
                                    options={options || []}
                                    onChange={(value) => {
                                        field.onChange(value)
                                        if (onChange) {
                                            onChange()
                                        }
                                    }}
                                    errorMessage={(errors[name] as FieldError)?.message}
                                >
                                    {label}
                                </Radio>
                            )

                        case 'checkbox':
                            return (
                                <Checkbox
                                    {...field}
                                    size={size}
                                    id={id}
                                    ref={ref}
                                    onChange={(e) => {
                                        field.onChange(e)
                                        if (onChange) {
                                            onChange()
                                        }
                                    }}
                                    errorMessage={(errors[name] as FieldError)?.message}
                                    isInvalid={!!errors[name]}
                                    isRequired={isRequired}
                                >
                                    {label}
                                </Checkbox>
                            )

                        default:
                            return (
                                <Input
                                    {...field}
                                    ref={ref}
                                    label={label}
                                    type={type}
                                    placeholder={placeholder}
                                    isDisabled={isDisabled}
                                    variant={variant}
                                    onChange={(e) => {
                                        field.onChange(e.target.value)
                                        if (onChange) {
                                            onChange()
                                        }
                                    }}
                                    isRequired={isRequired}
                                    errorMessage={(errors[name] as FieldError)?.message}
                                    data-intent-redact
                                />
                            )
                            break
                    }
                }}
            />
        )
    }

    const componentMap: Record<string, () => JSX.Element> = {
        text: () => renderInput(),
        email: () =>
            renderInput({
                pattern: {
                    value: /^[\w.%+-]+@[\w.]+\.[\w]{2,6}$/,
                    message: `${formatMessage({
                        id: 'label.newsletter.form.email.invalid',
                        defaultMessage: 'Error: enter a valid e-mail address',
                    })}`,
                },
            }),
        tel: () =>
            renderInput({
                pattern: {
                    value: /^(?=(?:[^0-9]*[0-9]){10})[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s0-9]*$/,
                    message: `${formatMessage({
                        id: 'error.message.account.form.phonenotvalid',
                        defaultMessage: 'Error: invalid phone number',
                    })}`,
                },
            }),
        password: () => renderInput(),
        textarea: () =>
            renderInput({
                required: emptyFieldErrorLabel,
            }),
        select: () =>
            renderInput({
                required: emptyFieldErrorLabel,
            }),
        radio: () => renderInput(),
        checkbox: () => renderInput(),
        preferenceNewsletterConsent: () =>
            renderInput({
                required: isRequired,
            }),
    }

    const Component = componentMap[type]

    return Component ? Component() : null
}

export {FormFactory, FormValues}
